import { useEffect, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CameraIcon,
  CheckIcon,
  LightbulbIcon,
  WarningIcon,
  XIcon,
} from "@phosphor-icons/react";
import perritoFoto from "../../TALLER/perrito-foto.webp";

const MAXIMO_FOTOS = Number.parseInt(import.meta.env.VITE_IMAGEN_MAX_POR_REPORTE ?? "4", 10);
const MAXIMO_BYTES = 3 * 1024 * 1024;
const ANCHO_MINIMO = 200;

const consejosBase = [
  "Buena iluminación",
  "De frente, de lado y cuerpo completo",
  "Enfoca características únicas",
  "Evita filtros o edición excesiva",
];

const variantes = {
  encontrada: {
    titulo: "Agrega fotos de la mascota",
    descripcion: "Las fotos claras y desde diferentes ángulos ayudan a identificarla.",
    consejos: consejosBase,
  },
  perdida: {
    titulo: "Agrega fotos de tu mascota",
    descripcion: "Usa fotos claras y actuales desde diferentes ángulos para ayudar a identificarla.",
    consejos: [...consejosBase.slice(0, 3), "Evita fotos borrosas"],
    imagen: perritoFoto,
  },
  persona: {
    titulo: "Agrega fotos de la persona",
    descripcion: "Usa fotos claras y recientes, de frente y desde diferentes ángulos para ayudar a identificarla.",
    consejos: [
      "Buena iluminación",
      "Rostro visible y sin filtros",
      "Usa fotografías recientes",
      "Agrega distintos ángulos si los tienes",
    ],
    entidad: "persona",
  },
};

function comienzaCon(bytes, firma, offset = 0) {
  return firma.every((byte, index) => bytes[offset + index] === byte);
}

function textoEn(bytes, texto, offset = 0) {
  return [...texto].every((caracter, index) => bytes[offset + index] === caracter.charCodeAt(0));
}

async function detectarFormato(archivo) {
  const bytes = new Uint8Array(await archivo.slice(0, 16).arrayBuffer());
  if (comienzaCon(bytes, [0xff, 0xd8, 0xff])) return "JPEG";
  if (comienzaCon(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "PNG";
  if (textoEn(bytes, "GIF87a") || textoEn(bytes, "GIF89a")) return "GIF";
  if (textoEn(bytes, "RIFF") && textoEn(bytes, "WEBP", 8)) return "WEBP";
  if (comienzaCon(bytes, [0x49, 0x49, 0x2a, 0x00]) || comienzaCon(bytes, [0x4d, 0x4d, 0x00, 0x2a])) return "TIFF";
  if (["ftypheic", "ftypheix", "ftypmif1", "ftypmsf1"].some((marca) => textoEn(bytes, marca, 4))) return "HEIC";
  if (textoEn(bytes, "ftypavif", 4)) return "AVIF";
  return null;
}

async function obtenerDimensiones(archivo) {
  if ("createImageBitmap" in window) {
    try {
      const bitmap = await createImageBitmap(archivo, { imageOrientation: "from-image" });
      const dimensiones = { ancho: bitmap.width, alto: bitmap.height };
      bitmap.close();
      return dimensiones;
    } catch {
      // Algunos navegadores decodifican HEIC/TIFF mediante <img>, pero no con ImageBitmap.
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(archivo);
    const imagen = new Image();
    imagen.onload = () => {
      resolve({ ancho: imagen.naturalWidth, alto: imagen.naturalHeight });
      URL.revokeObjectURL(url);
    };
    imagen.onerror = () => {
      reject(new Error("No se pudieron leer las dimensiones"));
      URL.revokeObjectURL(url);
    };
    imagen.src = url;
  });
}

async function validarFoto(archivo) {
  const formato = await detectarFormato(archivo);
  if (!formato) {
    throw new Error(`${archivo.name}: no es una imagen válida. Usa JPG, PNG, WEBP, GIF, TIFF, HEIC o AVIF.`);
  }
  if (archivo.size > MAXIMO_BYTES) {
    const peso = (archivo.size / 1024 / 1024).toFixed(1);
    throw new Error(`${archivo.name}: pesa ${peso} MB y el máximo permitido es 3 MB.`);
  }

  let dimensiones;
  try {
    dimensiones = await obtenerDimensiones(archivo);
  } catch {
    throw new Error(`${archivo.name}: el navegador no pudo comprobar sus dimensiones. Prueba con una foto JPG, PNG o WEBP.`);
  }
  if (dimensiones.ancho < ANCHO_MINIMO) {
    throw new Error(`${archivo.name}: mide ${dimensiones.ancho}px de ancho y el mínimo son ${ANCHO_MINIMO}px.`);
  }

  return {
    archivo,
    url: URL.createObjectURL(archivo),
    formato,
    ancho: dimensiones.ancho,
    alto: dimensiones.alto,
  };
}

function ModalAdvertencia({ mensajes, onClose }) {
  const botonRef = useRef(null);

  useEffect(() => {
    botonRef.current?.focus();
    function cerrarConEscape(evento) {
      if (evento.key === "Escape") onClose();
    }
    document.addEventListener("keydown", cerrarConEscape);
    return () => document.removeEventListener("keydown", cerrarConEscape);
  }, [onClose]);

  return (
    <div className="photo-modal" role="presentation" onMouseDown={(evento) => { if (evento.target === evento.currentTarget) onClose(); }}>
      <div className="photo-modal__dialog" role="alertdialog" aria-modal="true" aria-labelledby="photo-warning-title" aria-describedby="photo-warning-copy">
        <span className="photo-modal__icon"><WarningIcon weight="fill" /></span>
        <h2 id="photo-warning-title">Esta foto no se puede agregar</h2>
        <p id="photo-warning-copy">Revisa lo siguiente e intenta con otra imagen:</p>
        <ul>{mensajes.map((mensaje) => <li key={mensaje}>{mensaje}</li>)}</ul>
        <div className="photo-modal__requirements">
          <strong>Requisitos</strong>
          <span>Hasta 3 MB · Mínimo 200 px de ancho · Máximo {MAXIMO_FOTOS} fotos</span>
        </div>
        <button type="button" onClick={onClose} ref={botonRef}>Entendido</button>
      </div>
    </div>
  );
}

function FotosMascotaForm({ fotos, onChange, onBack, onNext, onMissing, variante = "encontrada" }) {
  const inputRef = useRef(null);
  const [advertencias, setAdvertencias] = useState([]);
  const [validando, setValidando] = useState(false);
  const configuracion = variantes[variante] ?? variantes.encontrada;
  const entidad = configuracion.entidad ?? "mascota";

  async function seleccionarFotos(evento) {
    const archivos = [...evento.target.files];
    evento.target.value = "";
    if (!archivos.length) return;

    const cupos = MAXIMO_FOTOS - fotos.length;
    const errores = [];
    if (archivos.length > cupos) {
      errores.push(`Solo puedes agregar ${cupos} foto${cupos === 1 ? "" : "s"} más.`);
    }

    setValidando(true);
    const nuevasFotos = [];
    for (const archivo of archivos.slice(0, cupos)) {
      try {
        nuevasFotos.push(await validarFoto(archivo));
      } catch (error) {
        errores.push(error.message);
      }
    }
    setValidando(false);
    if (nuevasFotos.length) onChange([...fotos, ...nuevasFotos]);
    if (errores.length) setAdvertencias(errores);
  }

  function quitarFoto(indice) {
    URL.revokeObjectURL(fotos[indice].url);
    onChange(fotos.filter((_, posicion) => posicion !== indice));
  }

  function continuar() {
    if (!fotos.length) {
      onMissing(["Al menos una foto"]);
      return;
    }
    onNext();
  }

  return (
    <section className={`photos-step photos-step--${variante}`} aria-labelledby="photos-title">
      <div className="photos-step__intro">
        <div><h2 id="photos-title">{configuracion.titulo}</h2><p>{configuracion.descripcion}</p></div>
        {configuracion.imagen && <img src={configuracion.imagen} alt={`Ilustración de fotografías de una ${entidad}`} />}
      </div>

      <div className="photos-field">
        <div className="photos-field__heading"><strong>Fotos <span>(mínimo 1)</span></strong><small>{fotos.length}/{MAXIMO_FOTOS}</small></div>
        <div className="photo-grid">
          {fotos.map((foto, index) => (
            <figure className="photo-preview" key={foto.url}>
              <img src={foto.url} alt={`Vista previa ${index + 1} de la ${entidad}`} />
              <button type="button" onClick={() => quitarFoto(index)} aria-label={`Quitar foto ${index + 1}`}><XIcon weight="bold" /></button>
              <figcaption>{foto.ancho} × {foto.alto}</figcaption>
            </figure>
          ))}
          {fotos.length < MAXIMO_FOTOS && (
            <button className="add-photo" type="button" onClick={() => inputRef.current?.click()} disabled={validando}>
              <CameraIcon weight="duotone" /><span>{validando ? "Revisando…" : "Agregar foto"}</span>
            </button>
          )}
        </div>
        <input ref={inputRef} className="sr-only" type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.tif,.tiff,.heic,.heif,.avif,image/*" multiple onChange={seleccionarFotos} />
        <p className="photos-field__note">Las fotos todavía no se subirán. Permanecerán en este dispositivo hasta la confirmación final.</p>
      </div>

      <aside className="photo-tips">
        <div><LightbulbIcon weight="duotone" /><strong>Consejos para mejores fotos</strong></div>
        <ul>{configuracion.consejos.map((consejo) => <li key={consejo}><CheckIcon weight="bold" />{consejo}</li>)}</ul>
      </aside>

      <div className="photos-step__actions">
        <button type="button" onClick={onBack}><ArrowLeftIcon weight="bold" /> Atrás</button>
        <button type="button" onClick={continuar}>Siguiente <ArrowRightIcon weight="bold" /></button>
      </div>

      {advertencias.length > 0 && <ModalAdvertencia mensajes={advertencias} onClose={() => setAdvertencias([])} />}
    </section>
  );
}

export default FotosMascotaForm;
